/**
 * FAQ Display Widget JavaScript
 *
 * This file contains the JavaScript functionality for the FAQ Display widget.
 * It handles search functionality and smooth scrolling for the table of contents.
 */

(function($) {
    'use strict';

    // Animation speed for all fade effects (in milliseconds)
    const fadeSpeed = 200;
    
    // Debounce delay for search input (in milliseconds)
    const debounceDelay = 300;
    
    /**
     * Debounce function to limit how often a function can be called
     * @param {Function} func - The function to debounce
     * @param {number} wait - The delay in milliseconds
     * @return {Function} - The debounced function
     */
    function debounce(func, wait) {
        let timeout;
        return function() {
            const context = this;
            const args = arguments;
            clearTimeout(timeout);
            timeout = setTimeout(function() {
                func.apply(context, args);
            }, wait);
        };
    }

    /**
     * Initialize FAQ Display functionality
     */
    function initFaqDisplay() {
        // Initialize search functionality
        initSearch();

        // Initialize table of contents smooth scrolling
        initTocScrolling();
    }

    /**
     * Initialize search functionality
     */
    function initSearch() {
        // Cache DOM elements to reduce repeated jQuery selections
        const $searchInput = $('.faq-search-input');
        const $clearButton = $('.faq-search-clear');
        const $faqToc = $('.faq-toc');
        const $faqContent = $('.faq-content');
        const $noResultsToc = $('.faq-no-results-toc');
        const $noResultsContent = $('.faq-no-results-content');
        const $faqPosts = $('.faq-post');
        const $faqPostTitles = $('.faq-post-title');
        const $faqPostTags = $('.faq-post-tags');
        const $faqCategories = $('.faq-category');
        const $faqSubcategories = $('.faq-subcategory');
        const $tocItems = $('.faq-toc li');

        // Create no results messages if they don't exist
        if ($noResultsToc.length === 0) {
            $faqToc.append('<p class="faq-no-results-toc" style="display: none;">' + 
                'No matching FAQs found. Please try a different search term.</p>');
        }

        if ($noResultsContent.length === 0) {
            $faqContent.append('<p class="faq-no-results-content" style="display: none;">' + 
                'No matching FAQs found. Please try a different search term.</p>');
        }

        // Function to perform search
        function performSearch() {
            var searchTerm = $searchInput.val().toLowerCase();
            var hasResults = false;

            // Toggle clear button visibility
            if (searchTerm.length > 0) {
                if (!$clearButton.is(':visible')) {
                    $clearButton.stop(true, true).fadeIn(fadeSpeed);
                }
            } else {
                if ($clearButton.is(':visible')) {
                    $clearButton.stop(true, true).fadeOut(fadeSpeed);
                }
            }

            // Hide no results messages initially
            $noResultsToc.stop(true, true).fadeOut(fadeSpeed);
            $noResultsContent.stop(true, true).fadeOut(fadeSpeed);

            if (searchTerm.length > 2) {
                // First, collect all posts that match the search term
                var matchingPosts = [];
                var matchingPostIds = {}; // Use object as a Set for faster lookups

                // Search in post titles (using cached elements)
                $faqPostTitles.each(function() {
                    var $this = $(this);
                    var postTitle = $this.text().toLowerCase();
                    if (postTitle.indexOf(searchTerm) !== -1) {
                        var $post = $this.closest('.faq-post');
                        var postId = $post.attr('id') || $post.index();
                        if (!matchingPostIds[postId]) {
                            matchingPostIds[postId] = true;
                            matchingPosts.push($post);
                        }
                    }
                });
                
                // Also search in post tags (using cached elements)
                $faqPostTags.each(function() {
                    var $this = $(this);
                    var postTags = $this.text().toLowerCase();
                    if (postTags.indexOf(searchTerm) !== -1) {
                        var $post = $this.closest('.faq-post');
                        var postId = $post.attr('id') || $post.index();
                        if (!matchingPostIds[postId]) {
                            matchingPostIds[postId] = true;
                            matchingPosts.push($post);
                        }
                    }
                });

                // If we have matching posts, show them and hide non-matching ones
                if (matchingPosts.length > 0) {
                    hasResults = true;
                    
                    // Create a map of visible posts for faster lookups
                    var visiblePostMap = {};
                    matchingPosts.forEach(function($post) {
                        var postId = $post.attr('id') || $post.index();
                        visiblePostMap[postId] = true;
                    });

                    // Hide all posts that don't match (using cached elements)
                    $faqPosts.each(function() {
                        var $post = $(this);
                        var postId = $post.attr('id') || $post.index();
                        
                        if (!visiblePostMap[postId] && $post.is(':visible')) {
                            $post.stop(true, true).fadeOut(fadeSpeed);
                        } else if (visiblePostMap[postId] && $post.is(':hidden')) {
                            $post.stop(true, true).fadeIn(fadeSpeed);
                        }
                    });

                    // Track which categories and subcategories have visible posts
                    var categoriesWithPosts = {};
                    var subcategoriesWithPosts = {};
                    
                    // Show matching posts and track their parent categories/subcategories
                    matchingPosts.forEach(function($post) {
                        var $category = $post.closest('.faq-category');
                        var $subcategory = $post.closest('.faq-subcategory');
                        
                        var categoryId = $category.attr('id') || $category.index();
                        categoriesWithPosts[categoryId] = true;
                        
                        if ($category.is(':hidden')) {
                            $category.stop(true, true).fadeIn(fadeSpeed);
                        }

                        if ($subcategory.length) {
                            var subcategoryId = $subcategory.attr('id') || $subcategory.index();
                            subcategoriesWithPosts[subcategoryId] = true;
                            
                            if ($subcategory.is(':hidden')) {
                                $subcategory.stop(true, true).fadeIn(fadeSpeed);
                            }
                        }
                    });

                    // Hide categories with no visible posts (using cached elements)
                    $faqCategories.each(function() {
                        var $category = $(this);
                        var categoryId = $category.attr('id') || $category.index();
                        
                        if (!categoriesWithPosts[categoryId] && $category.is(':visible')) {
                            $category.stop(true, true).fadeOut(fadeSpeed);
                        }
                    });

                    // Hide subcategories with no visible posts (using cached elements)
                    $faqSubcategories.each(function() {
                        var $subcategory = $(this);
                        var subcategoryId = $subcategory.attr('id') || $subcategory.index();
                        
                        if (!subcategoriesWithPosts[subcategoryId] && $subcategory.is(':visible')) {
                            $subcategory.stop(true, true).fadeOut(fadeSpeed);
                        }
                    });

                    // Filter TOC to only show categories that have visible posts
                    filterTocBasedOnSearch(categoriesWithPosts);
                } else {
                    // No results found
                    hasResults = false;

                    // Hide all content (using cached elements)
                    $faqCategories.filter(':visible').stop(true, true).fadeOut(fadeSpeed);
                    $faqSubcategories.filter(':visible').stop(true, true).fadeOut(fadeSpeed);
                    $faqPosts.filter(':visible').stop(true, true).fadeOut(fadeSpeed);

                    // Show no results messages
                    $noResultsToc.stop(true, true).fadeIn(fadeSpeed);
                    $noResultsContent.stop(true, true).fadeIn(fadeSpeed);
                }
            } else {
                // If search term is too short, show everything (using cached elements)
                $faqCategories.filter(':hidden').stop(true, true).fadeIn(fadeSpeed);
                $faqSubcategories.filter(':hidden').stop(true, true).fadeIn(fadeSpeed);
                $faqPosts.filter(':hidden').stop(true, true).fadeIn(fadeSpeed);

                // Reset TOC visibility
                $tocItems.filter(':hidden').stop(true, true).fadeIn(fadeSpeed);

                // Hide no results messages
                $noResultsToc.stop(true, true).fadeOut(fadeSpeed);
                $noResultsContent.stop(true, true).fadeOut(fadeSpeed);
            }
        }

        // Function to filter TOC based on search results
        function filterTocBasedOnSearch(categoriesWithPosts) {
            // Hide all TOC items initially, but only if they're visible
            $tocItems.filter(':visible').stop(true, true).fadeOut(fadeSpeed);

            // Show TOC items for categories that have visible posts
            $faqCategories.filter(':visible').each(function() {
                const categoryId = $(this).attr('id');
                if (categoryId) {
                    // Show the TOC item for this category
                    const $tocItem = $faqToc.find('a[href="#' + categoryId + '"]').closest('li');
                    if ($tocItem.is(':hidden')) {
                        $tocItem.stop(true, true).fadeIn(fadeSpeed);
                    }

                    // Also show parent items in the TOC hierarchy
                    $tocItem.parents('li').filter(':hidden').stop(true, true).fadeIn(fadeSpeed);
                }
            });
        }

        // Add keyup handler for search input with debouncing
        $searchInput.on('keyup', debounce(performSearch, debounceDelay));

        // Add click handler for search button
        $('.faq-search-button').on('click', function() {
            performSearch();
        });

        // Add click handler for clear button
        $clearButton.on('click', function() {
            $searchInput.val('');

            // Only hide the clear button if it's visible
            if ($clearButton.is(':visible')) {
                $clearButton.stop(true, true).fadeOut(fadeSpeed);
            }

            // Show all content, but only if it's hidden (using cached elements)
            $faqCategories.filter(':hidden').stop(true, true).fadeIn(fadeSpeed);
            $faqSubcategories.filter(':hidden').stop(true, true).fadeIn(fadeSpeed);
            $faqPosts.filter(':hidden').stop(true, true).fadeIn(fadeSpeed);

            // Show all TOC items, but only if they're hidden (using cached elements)
            $tocItems.filter(':hidden').stop(true, true).fadeIn(fadeSpeed);

            // Hide no results messages (using cached elements)
            $noResultsToc.stop(true, true).fadeOut(fadeSpeed);
            $noResultsContent.stop(true, true).fadeOut(fadeSpeed);
        });
    }

    /**
     * Initialize table of contents smooth scrolling
     */
    function initTocScrolling() {
            $('.faq-toc a').on('click', function(e) {
                e.preventDefault();
                const $target = $($(this).attr('href'));

                if (!$target.length) return;

                // Get header height dynamically
                const headerHeight = [
                    '#wpadminbar',  // More specific admin bar selector
                    '.listivo-menu-sticky-holder .listivo-menu-v2',  // Menu selector
                    '.listivo-menu-sticky-holder .listivo-menu-v2:visible',  // Only visible menu
                    '#leaderboard',  // Leaderboard selector
                    '#leaderboard:visible'  // Only visible leaderboard
                ].reduce((height, selector) => {
                    const $element = $(selector);
                    return height + ($element.is(':visible') ? Math.round($element.outerHeight()) || 0 : 0);
                }, 0);

                const additionalOffset = 20;
                const targetPosition = $target.offset().top - (headerHeight + additionalOffset);

                // Stop any ongoing animations before starting new one
                $('html, body').stop().animate({
                    scrollTop: targetPosition
                }, {
                    duration: fadeSpeed,
                    easing: 'swing',
                    complete: function() {
                        // Update URL hash after scroll complete
                        if (history.pushState) {
                            history.pushState(null, null, $target.attr('id') ? '#' + $target.attr('id') : '');
                        }
                    }
                });
            });
        }

    /**
     * Initialize when document is ready
     */
    $(document).ready(function() {
        initFaqDisplay();
    });

    /**
     * Initialize when Elementor frontend is initialized
     */
    $(window).on('elementor/frontend/init', function() {
        elementorFrontend.hooks.addAction('frontend/element_ready/faq_display.default', function($scope) {
            initFaqDisplay();
        });
    });

})(jQuery);
